import * as api from '@opentelemetry/api';
import { Attributes, AttributeValue, Exception, HrTime, Link, Status, TimedEvent, TimeInput, TraceState } from '@opentelemetry/api';
import { Endeavour } from './Endeavour';
import { hrTime, hrTimeDuration, InstrumentationLibrary, isTimeInput, timeInputToHrTime } from './OpenTelemetry/Core';
import { Resource } from './OpenTelemetry/Resources';
import { PipelineConfiguration } from './PipelineConfiguration';
import { isAttributeValue } from './Tracer';

declare enum SpanKind {
  INTERNAL = 0,
  SERVER = 1,
  CLIENT = 2,
  PRODUCER = 3,
  CONSUMER = 4,
}

/** Default limit for Message events per span */
export const DEFAULT_MAX_EVENTS_PER_SPAN = 128;
export const DEFAULT_MAX_ATTRIBUTES_PER_SPAN = 32;
export const DEFAULT_MAX_LINKS_PER_SPAN = 32;
export const INVALID_SPANID = '0000000000000000';
export const INVALID_TRACEID = '00000000000000000000000000000000';

interface SpanContext {
  traceId: string;
  spanId: string;
  isRemote?: boolean;
  traceFlags: number;
  traceState?: TraceState;
}

interface TraceParams {
  /** numberOfAttributesPerSpan is number of attributes per span */
  numberOfAttributesPerSpan?: number;
  /** numberOfLinksPerSpan is number of links per span */
  numberOfLinksPerSpan?: number;
  /** numberOfEventsPerSpan is number of message events per span */
  numberOfEventsPerSpan?: number;
}

export interface Context {
  getValue(key: symbol): unknown;
  setValue(key: symbol, value: unknown): Context;
  deleteValue(key: symbol): Context;
}

export const ExceptionAttribute = {
  MESSAGE: 'exception.message',
  STACKTRACE: 'exception.stacktrace',
  TYPE: 'exception.type',
};

export const ExceptionEventName = 'exception';

export interface ContextManager {
  active(): Context;
  with<T extends (...args: unknown[]) => ReturnType<T>>(context: Context, fn: T): ReturnType<T>;
  bind<T>(target: T, context?: Context): T;
  enable(): this;
  disable(): this;
}

export class BaseContext implements Context {
  private currentContext: Map<symbol, unknown>;

  /**
   * Construct a new context which inherits values from an optional parent context.
   *
   * @param parentContext a context from which to inherit values
   */
  public constructor(parentContext?: Map<symbol, unknown>) {
    this.currentContext = parentContext ? new Map(parentContext) : new Map();
  }

  /**
   * Get a value from the context.
   *
   * @param key key which identifies a context value
   */
  public getValue(key: symbol): unknown {
    return this.currentContext.get(key);
  }

  /**
   * Create a new context which inherits from this context and has
   * the given key set to the given value.
   *
   * @param key context key for which to set the value
   * @param value value to set for the given key
   */
  public setValue(key: symbol, value: unknown): Context {
    const context = new BaseContext(this.currentContext);
    context.currentContext.set(key, value);
    return context;
  }

  /**
   * Return a new context which inherits from this context but does
   * not contain a value for the given key.
   *
   * @param key context key for which to clear a value
   */
  public deleteValue(key: symbol): Context {
    const context = new BaseContext(this.currentContext);
    context.currentContext.delete(key);
    return context;
  }
}

export const ROOT_CONTEXT: Context = new BaseContext();

// tslint:disable-next-line: interface-name
interface ISpan {
  context(): SpanContext;
  setAttribute(key: string, value?: AttributeValue): this;
  setAttributes(attributes: Attributes): this;
  addEvent(name: string, attributesOrStartTime?: Attributes | TimeInput, startTime?: TimeInput): this;
  setStatus(status: Status): this;
  updateName(name: string): this;
  end(endTime?: TimeInput): void;
  isRecording(): boolean;
  recordException(exception: Exception, time?: TimeInput): void;
}

export interface ReadableSpan {
  readonly name: string;
  readonly kind: SpanKind;
  readonly spanContext: SpanContext;
  readonly parentSpanId?: string;
  readonly startTime: HrTime;
  readonly endTime: HrTime;
  readonly status: Status;
  readonly attributes: Attributes;
  readonly links: Link[];
  readonly events: TimedEvent[];
  readonly duration: HrTime;
  readonly ended: boolean;
  readonly resource: Resource;
  readonly instrumentationLibrary: InstrumentationLibrary;
}

/**
 * This class represents a span.
 */
export class Span implements ISpan, ReadableSpan {
  // Below properties are included to implement ReadableSpan for export
  // purposes but are not intended to be written-to directly.
  public readonly spanContext: api.SpanContext;
  public readonly kind: api.SpanKind;
  public readonly parentSpanId?: string;
  public readonly attributes: api.Attributes = {};
  public readonly links: api.Link[] = [];
  public readonly events: api.TimedEvent[] = [];
  public readonly startTime: api.HrTime;
  public readonly resource: Resource = new Resource({});
  public readonly instrumentationLibrary: InstrumentationLibrary = { name: 'foo', version: '0.666' };
  public name: string;
  public status: api.Status = {
    code: api.CanonicalCode.OK,
  };
  public endTime: api.HrTime = [0, 0];
  // tslint:disable-next-line: variable-name
  private _ended: boolean = false;
  // tslint:disable-next-line: variable-name
  private _duration: api.HrTime = [-1, -1];
  // tslint:disable-next-line: variable-name
  private readonly traceParams: TraceParams = {
    numberOfAttributesPerSpan: DEFAULT_MAX_ATTRIBUTES_PER_SPAN,
    numberOfLinksPerSpan: DEFAULT_MAX_LINKS_PER_SPAN,
    numberOfEventsPerSpan: DEFAULT_MAX_EVENTS_PER_SPAN,
  };

  /** Constructs a new Span instance. */
  public constructor(spanName: string, spanContext: SpanContext, kind: SpanKind, parentSpanId?: string, startTime: TimeInput = hrTime()) {
    this.name = spanName;
    this.spanContext = spanContext;
    this.parentSpanId = parentSpanId;
    this.kind = kind;
    this.startTime = timeInputToHrTime(startTime);
  }

  public context(): api.SpanContext {
    return this.spanContext;
  }

  public setAttribute(key: string, value?: AttributeValue): this;
  public setAttribute(key: string, value: unknown): this {
    if (value === null || this._isSpanEnded()) {
      return this;
    }
    if (key.length === 0) {
      return this;
    }
    if (!isAttributeValue(value)) {
      return this;
    }

    if (Object.keys(this.attributes).length >= this.traceParams.numberOfAttributesPerSpan!) {
      const attributeKeyToDelete = Object.keys(this.attributes).shift();
      if (attributeKeyToDelete) {
        delete this.attributes[attributeKeyToDelete];
      }
    }
    this.attributes[key] = value;
    return this;
  }

  public setAttributes(attributes: api.Attributes): this {
    for (const [k, v] of Object.entries(attributes)) {
      this.setAttribute(k, v);
    }
    return this;
  }

  /**
   *
   * @param name Span Name
   * @param [attributesOrStartTime] Span attributes or start time
   *     if type is {@type TimeInput} and 3rd param is undefined
   * @param [startTime] Specified start time for the event
   */
  public addEvent(name: string, attributesOrStartTime?: api.Attributes | api.TimeInput, startTime?: api.TimeInput): this {
    if (!PipelineConfiguration.isEventAllowed(name)) {
      return this;
    }
    if (this._isSpanEnded()) {
      return this;
    }
    if (this.events.length >= this.traceParams.numberOfEventsPerSpan!) {
      this.events.shift();
    }
    if (isTimeInput(attributesOrStartTime)) {
      if (typeof startTime === 'undefined') {
        startTime = attributesOrStartTime as api.TimeInput;
      }
      attributesOrStartTime = undefined;
    }
    if (typeof startTime === 'undefined') {
      startTime = hrTime();
    }
    this.events.push({
      name,
      attributes: attributesOrStartTime as api.Attributes,
      time: timeInputToHrTime(startTime),
    });
    return this;
  }

  public setStatus(status: api.Status): this {
    if (this._isSpanEnded()) {
      return this;
    }
    this.status = status;
    return this;
  }

  public updateName(name: string): this {
    if (this._isSpanEnded()) {
      return this;
    }
    this.name = name;
    return this;
  }

  public end(endTime: api.TimeInput = hrTime()): void {
    if (this._isSpanEnded()) {
      return;
    }
    this._ended = true;
    this.endTime = timeInputToHrTime(endTime);

    this._duration = hrTimeDuration(this.startTime, this.endTime);

    if (Endeavour.optedIn) {
      Endeavour.sendPostSpans([this]);
    }
  }

  public isRecording(): boolean {
    return true;
  }

  public recordException(exception: api.Exception, time: api.TimeInput = hrTime()): void {
    const attributes: api.Attributes = {};
    if (typeof exception === 'string') {
      attributes[ExceptionAttribute.MESSAGE] = exception;
    } else if (exception) {
      if (exception.code) {
        attributes[ExceptionAttribute.TYPE] = exception.code;
      } else if (exception.name) {
        attributes[ExceptionAttribute.TYPE] = exception.name;
      }
      if (exception.message) {
        attributes[ExceptionAttribute.MESSAGE] = exception.message;
      }
      if (exception.stack) {
        attributes[ExceptionAttribute.STACKTRACE] = exception.stack;
      }
    }

    // these are minimum requirements from spec
    if (attributes[ExceptionAttribute.TYPE] || attributes[ExceptionAttribute.MESSAGE]) {
      this.addEvent(ExceptionEventName, attributes as api.Attributes, time);
    }
  }

  public get duration(): api.HrTime {
    return this._duration;
  }

  public get ended(): boolean {
    return this._ended;
  }

  private _isSpanEnded(): boolean {
    return this._ended;
  }
}
